'use client';
import { OnbForm } from '@/components/form';
import { sendOnbEvent } from '@/components/newRelic';
import { resetErrors } from '@/redux/errorSlice';
import { connectedField } from '@/redux/fields';
import { showToast } from '@/redux/navigationSlice';
import { RootState } from '@/redux/store';
import { Button, Stack, Typography } from '@mui/material';
import { initializeStep, mobileVerification } from '@repo/onb-api';
import { Otp } from '@repo/ui';
import { failureDetails, formatter, getClientCookie } from '@repo/utils';
import { useRouter } from 'next/navigation';
import { useDispatch, useSelector } from 'react-redux';
import StepWrapper from './stepWrapper';

export default function MobileVerification({
  stepname: stepName,
}: {
  stepname: mobileVerification['name'];
}): React.JSX.Element {
  const router = useRouter();
  const dispatch = useDispatch();
  const mobilePhoneNumber = useSelector((state: RootState) => state.flowdata.variables?.mobilePhoneNumber);
  const instanceId = getClientCookie('instanceId');

  const resendOTP = () => {
    let error = false;
    let failure: ReturnType<typeof failureDetails> | undefined;
    if (instanceId) {
      sendOnbEvent({ type: 'interaction', subtype: 'resendOTP', data: { instanceId } });
      initializeStep<mobileVerification>({
        instanceId,
        name: stepName,
      })
        .then((response) => {
          if (response.hardError || response.softError) {
            error = true;
            failure = failureDetails(response);
          } else {
            dispatch(showToast({ text: 'Código reenviado', kind: 'success' }));
            dispatch(resetErrors());
          }
        })
        .catch((e) => {
          error = true;
          failure = failureDetails(undefined, e);
        })
        .finally(() => {
          // Same as the email step: a failed resend keeps the applicant here instead of ending the flow
          if (error) {
            sendOnbEvent({
              type: 'error',
              subtype: 'initStep',
              data: { stepName, instanceId, resend: true, ...failure },
            });
            dispatch(showToast({ text: 'No pudimos reenviar el código. Intentá de nuevo', kind: 'error' }));
          }
        });
    }
  };

  return (
    <StepWrapper>
      <Typography variant="h1">Ingresá el código que te enviamos</Typography>
      <Stack>
        <Typography component="p" variant="subtitle2">
          Te llegará por SMS y WhatsApp
          {mobilePhoneNumber && (
            <>
              {' al: '}
              <span style={{ fontWeight: 'bold' }}>{formatter(mobilePhoneNumber, 'phone')}</span>
            </>
          )}
        </Typography>
        <Button
          variant="link"
          sx={{ mt: 1 }}
          onClick={() =>
            router.push(
              process.env.NEXT_PUBLIC_ONB_INSTANCE_TYPE === 'onb-comercios'
                ? '/flow/MERCHANT_CONTACT_INFORMATION'
                : '/flow/CONTACT_INFORMATION',
            )
          }
        >
          Cambiar número
        </Button>
      </Stack>
      <OnbForm<mobileVerification> stepName={stepName!}>
        <Otp
          fields={4}
          resend={resendOTP}
          {...connectedField({
            stepName,
            name: 'smsOtp',
            required: true,
            errorHandler: {
              type: 'text',
              errorText: 'Ingresá un código válido',
              validate: (value) => value.length > 3,
            },
          })}
        />
      </OnbForm>
    </StepWrapper>
  );
}
